/**
 * 後台 — 訂單詳情 + 分批出貨(#10 Phase 1.5)
 *
 * 顯示:訂單編號 / 收件人 / 出貨進度(訂購·已出·剩)/ 出貨紀錄 / 明細 / 備註
 * Action:
 *   - 新增出貨批次(可只出部分品項,選物流 + 單號 + 出貨日期)→ 全出完自動轉「已出貨」
 *   - 標記已完成 / 已取消(加客服備註)
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import {
  adminAddShipment,
  adminGetOrder,
  adminJkopayRefund,
  adminMarkOrderPaid,
  adminUpdateOrder,
  adminUpdateOrderStatus,
  ADMIN_MUTABLE_STATUSES,
  CARRIER_LABELS,
  CARRIER_OPTIONS,
  ORDER_STATUS_LABELS,
} from "../../api/admin";
import { getProducts, type Order, type Product } from "../../api/cart";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";
import { todayTpe } from "../../utils/date";


interface EditItem {
  product_id: string;
  name: string;
  spec: string;
  price_each: number;
  qty: number;                 // 件(商業單位,單價以此計)
  mode: string;
  shipped_bottles: number;     // 已出貨瓶數(qty 下界 = ceil(shipped_bottles / bottle_count))
  bottle_count: number;        // 一件 = 幾瓶
}

interface EditForm {
  name: string;
  phone: string;
  city: string;
  district: string;
  detail: string;
  note: string;
  admin_note: string;
  items: EditItem[];
}


export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const showToast = useUIStore((s) => s.showToast);

  // 出貨批次表單
  const [batchQty, setBatchQty] = useState<Record<string, number>>({});
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [shipDate, setShipDate] = useState(() => todayTpe());
  const [batchNote, setBatchNote] = useState("");
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  // 狀態變更(完成 / 取消)
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [refunding, setRefunding] = useState(false);

  // 編輯模式 — 收件資訊 / 備註 / 明細
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [addPid, setAddPid] = useState("");
  const [saving, setSaving] = useState(false);
  // 折讓 / 折扣編輯 — 兩模式(折讓額 / 直接改實收),最後換算成折讓額送出
  const [discountMode, setDiscountMode] = useState<"amount" | "final">("amount");
  const [discountInput, setDiscountInput] = useState("");
  const [discountNote, setDiscountNote] = useState("");

  // 商品清單(加品項下拉用)— 只抓一次
  useEffect(() => {
    getProducts()
      .then((cats) => setProducts(cats.flatMap((c) => c.items)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    if (!id) return;
    (async () => {
      try {
        const o = await adminGetOrder(id);
        setOrder(o);
      } catch {
        showToast("載入失敗", "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, role, navigate, showToast]);

  // order 變更 → 重置本批出貨量為各品項剩餘數
  useEffect(() => {
    if (!order) return;
    const prog = shipmentProgress(order);
    const init: Record<string, number> = {};
    for (const it of prog.items) {
      if (it.remainingBottles > 0) init[itemKey(it.product_id, it.mode)] = it.remainingBottles;
    }
    setBatchQty(init);
  }, [order]);

  if (loading) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-muted">載入中…</div>;
  }
  if (!order) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4">
        <div className="text-muted">找不到此訂單</div>
        <button onClick={() => navigate("/admin/orders")} className="btn-primary">返回列表</button>
      </div>
    );
  }

  const addr = order.address || {};
  const addrLine1 = [addr.city, addr.district].filter(Boolean).join(" ");
  const addrLine2 = addr.detail || "";

  const prog = shipmentProgress(order);
  const shipments = order.shipments || [];
  const isClosed = ["completed", "cancelled"].includes(order.status);
  const legacyShipInfo =
    shipments.length === 0 &&
    (order.ship_date || (order.tracking && (order.tracking.carrier || order.tracking.number)));

  async function submitBatch() {
    if (!order) return;
    const p = shipmentProgress(order);
    const lines = p.items
      .filter((it) => it.remainingBottles > 0)
      .map((it) => {
        const k = itemKey(it.product_id, it.mode);
        // qty = 本批瓶數(夾在 0 ~ 剩餘瓶數)
        const qty = Math.max(0, Math.min(it.remainingBottles, Number(batchQty[k] ?? it.remainingBottles) || 0));
        return { product_id: it.product_id, mode: it.mode, qty };
      })
      .filter((l) => l.qty > 0);

    if (lines.length === 0) {
      showToast("請至少填一項出貨數量", "error");
      return;
    }
    if (!carrier) {
      showToast("請選擇物流方式", "error");
      return;
    }
    if (!shipDate) {
      showToast("請填出貨日期", "error");
      return;
    }
    setBatchSubmitting(true);
    try {
      const r = await adminAddShipment(order.order_id, {
        lines,
        carrier,
        number: trackingNo.trim(),
        ship_date: shipDate,
        note: batchNote.trim(),
      });
      if (r.status === "ok") {
        const done = (r.remaining_total ?? 0) === 0;
        showToast(
          done ? "已全數出貨(已通知客戶)" : `已出貨一批,尚餘 ${r.remaining_total} 瓶(已通知客戶)`,
          "success",
        );
        // 存摺提領沒完整落帳(餘額不足 / 未入帳單)→ 出貨已成立,但要提醒 admin 對帳
        const wd = r.wallet_debit?.status;
        if (wd === "partial_failed" || wd === "exception") {
          showToast("注意:存摺提領未完整扣帳(餘額不足?),請到會員存摺確認", "error");
        } else if (wd === "no_credit_skip") {
          showToast("提醒:此單尚未入帳(未付款),本批未扣存摺", "error");
        }
        const o = await adminGetOrder(order.order_id);
        setOrder(o);
        setCarrier("");
        setTrackingNo("");
        setBatchNote("");
        setShipDate(todayTpe());
      } else if (r.status === "over_ship") {
        showToast(`出貨數量超出剩餘(剩 ${r.remaining} 瓶)`, "error");
      } else if (r.status === "invalid_carrier") {
        showToast("物流商代號不正確", "error");
      } else if (r.status === "invalid_ship_date") {
        showToast("出貨日期格式不正確", "error");
      } else if (r.status === "not_shippable") {
        showToast("此訂單目前不可出貨", "error");
      } else {
        showToast(`出貨未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setBatchSubmitting(false);
    }
  }

  async function markPaid() {
    if (!order) return;
    const ac: any = order.applied_code;
    const willReward = !!ac && (ac.type === "member" || ((ac.referee_bottles || 0) + (ac.referrer_bottles || 0)) > 0);
    const extra = willReward ? "(含獎勵瓶數發放)" : "";
    if (!confirm(`確認此訂單已收款?將觸發燕窩存摺入帳${extra}。`)) return;
    setPaying(true);
    try {
      const r = await adminMarkOrderPaid(order.order_id);
      if (r.status === "ok") {
        showToast("已標記付款,存摺已入帳", "success");
        const o = await adminGetOrder(order.order_id);
        setOrder(o);
      } else if (r.status === "already_paid") {
        showToast("此訂單已是付款狀態", "error");
      } else {
        showToast(`未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setPaying(false);
    }
  }

  async function jkopayRefund() {
    if (!order || refunding) return;
    const amt = order.total.toLocaleString();
    if (!confirm(`確認退款?NT$ ${amt} 將全額退回購買人的街口帳戶,訂單自動取消(沖回存摺瓶數並通知客戶)。此操作不可復原。`)) return;
    setRefunding(true);
    try {
      const r = await adminJkopayRefund(order.order_id);
      if (r.status === "ok") {
        showToast(`退款 NT$ ${amt} 成功,訂單已取消(已通知客戶)`, "success");
        const o = await adminGetOrder(order.order_id);
        setOrder(o);
      } else if (r.status === "refund_failed") {
        showToast("街口退款未成功,請稍後再試(可重按,退款序號會自動換新)", "error");
      } else if (r.status === "not_jkopay_paid_order") {
        showToast("此單非街口收款,不適用街口退款", "error");
      } else {
        showToast(`退款未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常,退款是否成功請至訂單頁確認後再重試", "error");
    } finally {
      setRefunding(false);
    }
  }

  async function applyStatus() {
    if (!newStatus || !order) return;
    setStatusSubmitting(true);
    try {
      const r = await adminUpdateOrderStatus(order.order_id, newStatus, note.trim());
      if (r.status === "ok") {
        const pushHint = newStatus === "cancelled" ? "(已通知客戶)" : "";
        showToast(`狀態已更新${pushHint}`, "success");
        const o = await adminGetOrder(order.order_id);
        setOrder(o);
        setNewStatus("");
        setNote("");
      } else {
        showToast(`更新未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setStatusSubmitting(false);
    }
  }

  function startEdit() {
    if (!order) return;
    const p = shipmentProgress(order);
    setEditForm({
      name: order.name || "",
      phone: order.phone || "",
      city: order.address?.city || "",
      district: order.address?.district || "",
      detail: order.address?.detail || "",
      note: order.note || "",
      admin_note: order.admin_note || "",
      items: p.items.map((it) => ({
        product_id: it.product_id,
        name: it.name,
        spec: it.spec,
        price_each: it.price_each,
        qty: it.qty,
        mode: it.mode,
        shipped_bottles: it.shippedBottles,
        bottle_count: it.bottle_count,
      })),
    });
    // 折讓初值 — 帶入原單既有折讓額(預設「折讓金額」模式)
    setDiscountMode("amount");
    setDiscountInput(order.discount_amount ? String(order.discount_amount) : "");
    setDiscountNote(order.discount_note || "");
    setAddPid("");
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setEditForm(null);
  }

  function updateEditField<K extends keyof EditForm>(k: K, v: EditForm[K]) {
    setEditForm((f) => (f ? { ...f, [k]: v } : f));
  }

  function updateItemQty(idx: number, qty: number) {
    setEditForm((f) => {
      if (!f) return f;
      const items = f.items.slice();
      const it = items[idx];
      const minUnits = Math.max(1, Math.ceil(it.shipped_bottles / (it.bottle_count || 1)));  // 下界 = 已出瓶數換算的件數 or 1
      const safe = Math.max(minUnits, qty);
      items[idx] = { ...it, qty: safe };
      return { ...f, items };
    });
  }

  function removeItem(idx: number) {
    setEditForm((f) => {
      if (!f) return f;
      const it = f.items[idx];
      if (it.shipped_bottles > 0) return f;                  // 已出貨不可刪
      return { ...f, items: f.items.filter((_, i) => i !== idx) };
    });
  }

  function addItem() {
    if (!editForm || !addPid) return;
    const p = products.find((x) => x.id === addPid);
    if (!p) return;
    // 同 product_id + one_time 已存在 → 數量 +1,否則加新行
    const dupIdx = editForm.items.findIndex(
      (it) => it.product_id === addPid && it.mode === "one_time",
    );
    if (dupIdx >= 0) {
      updateItemQty(dupIdx, editForm.items[dupIdx].qty + 1);
    } else {
      setEditForm({
        ...editForm,
        items: [
          ...editForm.items,
          {
            product_id: p.id,
            name: p.name,
            spec: p.spec,
            price_each: p.price,
            qty: 1,
            mode: "one_time",
            shipped_bottles: 0,
            bottle_count: 1,   // 佔位;存檔重載後由後台補正確 bottle_count
          },
        ],
      });
    }
    setAddPid("");
  }

  async function saveEdit() {
    if (!order || !editForm) return;
    if (!editForm.items.length) {
      showToast("明細至少要一項", "error");
      return;
    }
    setSaving(true);
    try {
      const r = await adminUpdateOrder(order.order_id, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        address: {
          city: editForm.city.trim(),
          district: editForm.district.trim(),
          detail: editForm.detail.trim(),
        },
        note: editForm.note.trim(),
        admin_note: editForm.admin_note.trim(),
        items: editForm.items.map((it) => ({
          product_id: it.product_id,
          qty: it.qty,
          mode: it.mode,
        })),
        discount_amount: editDiscount,
        discount_note: editDiscount > 0 ? discountNote.trim() : "",
      });
      if (r.status === "ok") {
        showToast("已儲存", "success");
        if (r.order) setOrder(r.order);
        else {
          const o = await adminGetOrder(order.order_id);
          setOrder(o);
        }
        setEditing(false);
        setEditForm(null);
      } else if (r.status === "qty_below_shipped") {
        showToast(`某品項數量低於已出貨 ${r.shipped_bottles} 瓶(至少訂 ${r.min_qty} 件)`, "error");
      } else if (r.status === "cannot_remove_shipped_item") {
        showToast("已出貨的品項不可移除", "error");
      } else if (r.status === "product_not_found") {
        showToast("找不到此商品", "error");
      } else if (r.status === "not_editable") {
        showToast("此狀態不可編輯", "error");
      } else if (r.status === "no_items") {
        showToast("明細至少要一項", "error");
      } else {
        showToast(`未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSaving(false);
    }
  }

  // 編輯期間的原價 / 折讓 / 實收即時換算(兩模式最後都收斂成折讓額,夾在 [0, 原價])
  const editSubtotal = editForm ? editForm.items.reduce((s, it) => s + it.price_each * it.qty, 0) : 0;
  const editDiscount = (() => {
    const v = Math.round(Number(discountInput) || 0);
    if (v <= 0) return 0;
    if (discountMode === "amount") return Math.min(v, editSubtotal);
    return Math.max(0, editSubtotal - Math.min(v, editSubtotal));
  })();
  const editFinal = Math.max(0, editSubtotal - editDiscount);

  return (
    <div className="min-h-screen bg-cream pb-12">
      <header className="bg-primary text-white px-6 py-5 flex items-center gap-3">
        <button onClick={() => navigate("/admin/orders")} className="text-white text-xl">‹</button>
        <h1 className="text-xl font-bold">訂單詳情</h1>
        {!isClosed && !editing && (
          <button
            onClick={startEdit}
            className="ml-auto text-sm border border-white/40 rounded px-3 py-1 hover:bg-white/10"
          >
            編輯
          </button>
        )}
        {editing && (
          <div className="ml-auto flex gap-2">
            <button
              onClick={cancelEdit}
              disabled={saving}
              className="text-sm border border-white/40 rounded px-3 py-1 hover:bg-white/10 disabled:opacity-50"
            >
              取消
            </button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="text-sm bg-white text-primary rounded px-3 py-1 font-medium hover:bg-cream disabled:opacity-50"
            >
              {saving ? "儲存中…" : "儲存"}
            </button>
          </div>
        )}
      </header>

      <div className="px-6 mt-6 space-y-4 max-w-3xl">
        {/* 訂單編號 + 狀態 */}
        <div className="card flex items-center justify-between">
          <div>
            <div className="text-sm text-muted">訂單編號</div>
            <div className="text-lg font-mono mt-1">#{order.order_id}</div>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted">目前狀態</div>
            <div className="text-lg font-bold text-ink mt-1">
              {ORDER_STATUS_LABELS[order.status] || order.status}
            </div>
          </div>
        </div>

        {/* 付款 + 使用的碼 */}
        {(order.applied_code || order.status === "pending_payment_mock") && (
          <div className="card space-y-3">
            {order.applied_code && (
              <div className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                <span className="text-muted">使用碼</span>
                <span>
                  <span className="font-mono">{order.applied_code.code}</span>
                  <span className="text-muted ml-2">
                    ({codeTypeLabel(order.applied_code.type)}
                    {order.applied_code.label ? ` · ${order.applied_code.label}` : ""})
                  </span>
                </span>
                {(order.discount_amount || 0) > 0 && (
                  <>
                    <span className="text-muted">折抵</span>
                    <span className="text-coral">− NT$ {(order.discount_amount || 0).toLocaleString()}</span>
                  </>
                )}
              </div>
            )}
            {order.status === "pending_payment_mock" && (
              <div>
                <button onClick={markPaid} disabled={paying} className="btn-primary">
                  {paying ? "處理中…" : "標記已付款"}
                </button>
                <div className="text-xxs text-muted mt-1">
                  確認收款後按此 → 燕窩存摺入帳
                  {(() => {
                    const ac: any = order.applied_code;
                    return !!ac && (ac.type === "member" || ((ac.referee_bottles || 0) + (ac.referrer_bottles || 0)) > 0)
                      ? " + 獎勵瓶數發放" : "";
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* 收件資訊 */}
        <div className="card space-y-2">
          <div className="text-sm text-muted font-medium mb-1">收件資訊</div>
          {editing && editForm ? (
            <div className="space-y-3">
              <div>
                <label className="label">收件人</label>
                <input
                  className="input"
                  value={editForm.name}
                  onChange={(e) => updateEditField("name", e.target.value)}
                />
              </div>
              <div>
                <label className="label">聯絡電話</label>
                <input
                  className="input"
                  value={editForm.phone}
                  onChange={(e) => updateEditField("phone", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">縣市</label>
                  <input
                    className="input"
                    value={editForm.city}
                    onChange={(e) => updateEditField("city", e.target.value)}
                  />
                </div>
                <div>
                  <label className="label">區</label>
                  <input
                    className="input"
                    value={editForm.district}
                    onChange={(e) => updateEditField("district", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="label">地址詳細</label>
                <input
                  className="input"
                  value={editForm.detail}
                  onChange={(e) => updateEditField("detail", e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <span className="text-muted">收件人</span><span>{order.name || "—"}</span>
              <span className="text-muted">聯絡電話</span><span className="font-mono">{order.phone || "—"}</span>
              <span className="text-muted">寄送地址</span><span>{addrLine1 || "—"}</span>
              {addrLine2 && <><span></span><span>{addrLine2}</span></>}
            </div>
          )}
        </div>

        {/* 出貨紀錄(分批出貨) */}
        {shipments.length > 0 && (
          <div className="card space-y-3">
            <div className="text-sm text-muted font-medium">
              出貨紀錄(共 {shipments.length} 批)
            </div>
            {shipments.map((sh, idx) => (
              <div key={sh.shipment_id} className="border border-line rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-ink">第 {idx + 1} 批</span>
                  <span className="text-xs text-muted">{sh.ship_date || "—"}</span>
                </div>
                <div className="text-xs text-muted">
                  {CARRIER_LABELS[sh.carrier] || sh.carrier || "未填物流"}
                  {sh.number ? ` · 單號 ${sh.number}` : ""}
                </div>
                <div className="text-xs space-y-0.5 pt-1">
                  {sh.lines.map((ln) => (
                    <div key={ln.product_id + ln.mode} className="flex justify-between">
                      <span>{ln.name}</span>
                      <span className="text-muted">× {ln.bottles ?? ln.qty} 瓶</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 出貨資訊(舊單單筆出貨 legacy 顯示) */}
        {legacyShipInfo && (
          <div className="card space-y-2">
            <div className="text-sm text-muted font-medium mb-1">出貨資訊</div>
            <div className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              {order.ship_date && (
                <>
                  <span className="text-muted">出貨日期</span>
                  <span className="font-medium">{order.ship_date}</span>
                </>
              )}
              {order.tracking?.carrier && (
                <>
                  <span className="text-muted">物流方式</span>
                  <span>{CARRIER_LABELS[order.tracking.carrier] || order.tracking.carrier}</span>
                </>
              )}
              {order.tracking?.number && (
                <>
                  <span className="text-muted">追蹤單號</span>
                  <span className="font-mono">{order.tracking.number}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* 訂單明細(含出貨進度 已出 / 剩) */}
        <div className="card">
          <div className="text-sm text-muted font-medium mb-3">訂單明細</div>
          {editing && editForm ? (
            <>
              <div className="space-y-2">
                {editForm.items.map((it, idx) => (
                  <div key={it.product_id + it.mode + idx} className="border border-line rounded-lg p-3 space-y-1">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm">{it.name}</div>
                        <div className="text-xs text-muted">
                          {it.spec} · NT$ {it.price_each.toLocaleString()}
                          {it.shipped_bottles > 0 && (
                            <span className="text-coral ml-2">已出 {it.shipped_bottles} 瓶(下界)</span>
                          )}
                        </div>
                      </div>
                      <input
                        type="number"
                        min={Math.max(1, Math.ceil(it.shipped_bottles / (it.bottle_count || 1)))}
                        className="input w-20 text-right"
                        value={it.qty}
                        onChange={(e) => updateItemQty(idx, Number(e.target.value) || 0)}
                      />
                      <button
                        onClick={() => removeItem(idx)}
                        disabled={it.shipped_bottles > 0}
                        className="text-coral text-xs px-2 py-1 disabled:opacity-30"
                        title={it.shipped_bottles > 0 ? "已出貨,不可移除" : "移除"}
                        aria-label="移除品項"
                      >
                        <X size={18} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <select
                  className="input flex-1"
                  value={addPid}
                  onChange={(e) => setAddPid(e.target.value)}
                >
                  <option value="">加品項…</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · NT$ {p.price.toLocaleString()}
                    </option>
                  ))}
                </select>
                <button
                  onClick={addItem}
                  disabled={!addPid}
                  className="btn-secondary disabled:opacity-50"
                >
                  ＋ 加入
                </button>
              </div>
              <div className="mt-3 space-y-2">
                <div className="text-right text-sm">
                  <span className="text-muted mr-3">新小計</span>
                  <span className="font-medium">NT$ {editSubtotal.toLocaleString()}</span>
                </div>
                <div>
                  <label className="label">折讓 / 折扣</label>
                  <div className="flex gap-2">
                    <select
                      className="input w-32 shrink-0"
                      value={discountMode}
                      onChange={(e) => setDiscountMode(e.target.value as "amount" | "final")}
                    >
                      <option value="amount">折讓金額</option>
                      <option value="final">直接改實收</option>
                    </select>
                    <input
                      className="input flex-1 text-right"
                      type="number"
                      min={0}
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value)}
                      placeholder={discountMode === "amount" ? "折多少(NT$)" : "實收金額(NT$)"}
                    />
                  </div>
                  {editDiscount > 0 && (
                    <input
                      className="input mt-2"
                      value={discountNote}
                      onChange={(e) => setDiscountNote(e.target.value)}
                      placeholder="折讓原因(選填,例:熟客回饋 / 活動折扣)"
                    />
                  )}
                </div>
                {editDiscount > 0 && (
                  <div className="text-right text-sm text-coral">
                    <span className="mr-3">折讓</span>
                    <span>− NT$ {editDiscount.toLocaleString()}</span>
                  </div>
                )}
                <div className="text-right">
                  <span className="text-muted mr-3 text-sm">新實收</span>
                  <span className="text-lg font-bold text-ink">NT$ {editFinal.toLocaleString()}</span>
                </div>
                {(order.status === "paid" || order.status === "partially_shipped") && editFinal !== order.total && (
                  <div className="text-right text-xxs text-muted">
                    此單已計入業績,儲存後會員累計消費同步 {editFinal > order.total ? "+" : "−"} NT$ {Math.abs(editFinal - order.total).toLocaleString()}
                  </div>
                )}
              </div>
            </>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted text-xs border-b border-line">
                  <th className="text-left py-2">品名</th>
                  <th className="text-left py-2">規格</th>
                  <th className="text-right py-2">單價</th>
                  <th className="text-right py-2">數量</th>
                  <th className="text-right py-2">已出(瓶)</th>
                  <th className="text-right py-2">剩(瓶)</th>
                  <th className="text-right py-2">小計</th>
                </tr>
              </thead>
              <tbody>
                {prog.items.map((it) => (
                  <tr key={it.product_id + it.mode} className="border-b border-line/50">
                    <td className="py-2">{it.name}</td>
                    <td className="py-2 text-muted">{it.spec}</td>
                    <td className="py-2 text-right">NT$ {it.price_each.toLocaleString()}</td>
                    <td className="py-2 text-right">{it.qty}</td>
                    <td className="py-2 text-right">{it.shippedBottles}</td>
                    <td className={`py-2 text-right ${it.remainingBottles > 0 ? "text-coral font-medium" : "text-muted"}`}>
                      {it.remainingBottles}
                    </td>
                    <td className="py-2 text-right">NT$ {it.subtotal.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {(order.discount_amount || 0) > 0 && (
                  <>
                    <tr>
                      <td colSpan={6} className="pt-3 text-right text-muted text-sm">小計</td>
                      <td className="pt-3 text-right text-sm">NT$ {(order.subtotal ?? order.total).toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td colSpan={6} className="text-right text-coral text-sm">
                        折讓{order.applied_code?.code
                          ? `(${order.applied_code.code})`
                          : order.discount_note ? `(${order.discount_note})` : ""}
                      </td>
                      <td className="text-right text-coral text-sm">− NT$ {(order.discount_amount || 0).toLocaleString()}</td>
                    </tr>
                  </>
                )}
                <tr>
                  <td colSpan={6} className="pt-2 text-right text-muted">{(order.discount_amount || 0) > 0 ? "應付" : "總計"}</td>
                  <td className="pt-2 text-right text-lg font-bold text-ink">NT$ {order.total.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* 客戶備註 + 管理員備註 */}
        {editing && editForm ? (
          <div className="card space-y-3">
            <div>
              <label className="label">客戶備註</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={editForm.note}
                onChange={(e) => updateEditField("note", e.target.value)}
                placeholder="客戶留的備註"
              />
            </div>
            <div>
              <label className="label">管理員備註</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={editForm.admin_note}
                onChange={(e) => updateEditField("admin_note", e.target.value)}
                placeholder="內部追蹤用,客戶看不到"
              />
            </div>
          </div>
        ) : (
          <>
            {order.note && (
              <div className="card">
                <div className="text-sm text-muted font-medium mb-2">客戶備註</div>
                <div className="text-sm text-ink whitespace-pre-wrap">{order.note}</div>
              </div>
            )}
            {order.admin_note && (
              <div className="card">
                <div className="text-sm text-muted font-medium mb-2">管理員備註</div>
                <div className="text-sm text-ink whitespace-pre-wrap">{order.admin_note}</div>
              </div>
            )}
          </>
        )}

        {/* 新增出貨批次(還有剩餘 + 未結案 + 非編輯模式) */}
        {prog.remainingTotalBottles > 0 && !isClosed && !editing && (
          <div className="card space-y-3">
            <div className="text-sm text-muted font-medium">新增出貨批次</div>
            <div className="text-xs text-muted">
              填本次要出貨的瓶數(可只出部分、可拆箱),選物流並填單號。剩餘 {prog.remainingTotalBottles} 瓶。
            </div>

            <div className="space-y-2">
              {prog.items.filter((it) => it.remainingBottles > 0).map((it) => {
                const k = itemKey(it.product_id, it.mode);
                return (
                  <div key={k} className="flex items-center gap-3">
                    <div className="flex-1 text-sm">
                      <div>{it.name}</div>
                      <div className="text-xs text-muted">
                        訂購 {it.orderedBottles} 瓶 · 已出 {it.shippedBottles} 瓶 · 剩 {it.remainingBottles} 瓶
                      </div>
                    </div>
                    <input
                      type="number"
                      min={0}
                      max={it.remainingBottles}
                      className="input w-20 text-right"
                      value={batchQty[k] ?? it.remainingBottles}
                      onChange={(e) => {
                        const v = Math.max(0, Math.min(it.remainingBottles, Number(e.target.value) || 0));
                        setBatchQty((m) => ({ ...m, [k]: v }));
                      }}
                    />
                    <span className="text-xs text-muted w-6">瓶</span>
                  </div>
                );
              })}
            </div>

            <div>
              <label className="label">物流方式 *</label>
              <select className="input" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
                <option value="">請選擇</option>
                {CARRIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">追蹤單號(選填)</label>
              <input
                className="input"
                value={trackingNo}
                onChange={(e) => setTrackingNo(e.target.value)}
                placeholder="物流追蹤號碼"
              />
            </div>
            <div>
              <label className="label">出貨日期 *</label>
              <input
                className="input"
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
              />
              <div className="text-xxs text-muted mt-1">預設今天,補登舊批可改</div>
            </div>
            <div>
              <label className="label">備註(選填)</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={batchNote}
                onChange={(e) => setBatchNote(e.target.value)}
                placeholder="例如:本批先出常溫品項"
              />
            </div>
            <div className="text-xs text-coral bg-coral/5 px-2 py-1 rounded">
              送出後自動推送出貨通知到客戶 LINE chat;未出完會註明尚有剩餘件數
            </div>
            <button onClick={submitBatch} disabled={batchSubmitting} className="btn-primary">
              {batchSubmitting ? "出貨中…" : "確認出貨"}
            </button>
          </div>
        )}

        {/* 狀態變更(完成 / 取消,僅未結案,非編輯模式) */}
        {!isClosed && !editing && (
          <div className="card space-y-3">
            <div className="text-sm text-muted font-medium">狀態變更</div>
            <div>
              <label className="label">改為</label>
              <select className="input" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                <option value="">請選擇</option>
                {ADMIN_MUTABLE_STATUSES.filter((s) => s !== "shipped").map((s) => (
                  <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>

            {newStatus === "cancelled" && order.payment?.provider === "jkopay" && (
              <div className="text-xs text-white bg-coral px-3 py-2 rounded font-medium">
                <AlertTriangle size={14} className="inline -mt-0.5 mr-1" aria-hidden="true" />此單已收街口款 NT$ {order.total.toLocaleString()} — 直接取消「不會退款」,錢會留在店家帳上。
                要退錢給客人請改用下方「街口支付退款」按鈕(退款成功會自動取消訂單)。
              </div>
            )}
            {newStatus === "cancelled" && (
              <div className="text-xs text-coral bg-coral/5 px-2 py-1 rounded">
                套用後將自動推送撤回通知到客戶 LINE chat,客服備註會作為「撤回說明」一併顯示
              </div>
            )}

            <div>
              <label className="label">客服備註(選填)</label>
              <textarea
                className="input resize-none"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="例如:已完成 / 取消原因"
              />
            </div>
            <button
              onClick={applyStatus}
              disabled={!newStatus || statusSubmitting}
              className="btn-primary"
            >
              {statusSubmitting ? "更新中…" : "套用狀態"}
            </button>
          </div>
        )}

        {/* 街口退款 — 只有街口收款單出現;歷史紀錄永遠顯示,按鈕僅未結案時給 */}
        {order.payment?.provider === "jkopay" && (
          <div className="card space-y-3">
            <div className="text-sm text-muted font-medium">街口支付退款</div>
            {(order.payment.refunds?.length ?? 0) > 0 && (
              <div className="space-y-1">
                {order.payment.refunds!.map((r, i) => (
                  <div key={i} className="flex justify-between items-center text-xs bg-sage-light/20 rounded px-2 py-1.5">
                    <span>已退款 NT$ {r.amount.toLocaleString()}</span>
                    <span className="text-muted">{r.refund_time || ""}{r.refund_tradeNo ? ` · ${r.refund_tradeNo}` : ""}</span>
                  </div>
                ))}
              </div>
            )}
            {!isClosed && !editing && (
              <>
                <div className="text-xs text-muted bg-sage-light/20 p-3 rounded-lg">
                  全額退款會把 NT$ {order.total.toLocaleString()} 退回購買人的街口帳戶,
                  並自動取消訂單、沖回存摺瓶數、LINE 通知客戶。街口規則:付款後 180 天內可退。
                </div>
                <button onClick={jkopayRefund} disabled={refunding} className="btn-primary bg-jkopay w-full">
                  {refunding ? "退款中…" : `全額退款並取消 · NT$ ${order.total.toLocaleString()}`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


/** 品項唯一 key — 同商品不同購買模式分開計(對齊後端 _item_key) */
function itemKey(productId: string, mode: string): string {
  return `${productId}|${mode}`;
}

/** 從 order.items + order.shipments 算每品項 已出 / 剩餘(皆以「瓶」計)+ 總剩餘瓶數。
 *  出貨以瓶為單位:訂購 it.qty(件)× bottle_count = 訂購瓶數;已出累計各批換算後的瓶數。
 *  bottle_count / line.bottles 由後台 GET 動態補上;舊批(無 unit)用 ln.qty × bottle_count 換算。 */
function shipmentProgress(order: Order) {
  const bpu: Record<string, number> = {};
  for (const it of order.items) bpu[it.product_id] = it.bottle_count ?? 1;
  const shipped: Record<string, number> = {};
  for (const sh of order.shipments || []) {
    for (const ln of sh.lines || []) {
      const k = itemKey(ln.product_id, ln.mode);
      const b = ln.bottles ?? (ln.unit === "bottle" ? ln.qty : ln.qty * (bpu[ln.product_id] ?? 1));
      shipped[k] = (shipped[k] || 0) + b;
    }
  }
  const items = order.items.map((it) => {
    const k = itemKey(it.product_id, it.mode);
    const per = it.bottle_count ?? 1;
    const orderedBottles = it.qty * per;
    const done = Math.min(shipped[k] || 0, orderedBottles);
    return {
      ...it,
      bottle_count: per,
      orderedBottles,
      shippedBottles: done,
      remainingBottles: Math.max(0, orderedBottles - done),
    };
  });
  const remainingTotalBottles = items.reduce((s, it) => s + it.remainingBottles, 0);
  return { items, remainingTotalBottles };
}

/** 碼類型中文 label */
function codeTypeLabel(t: string): string {
  return t === "member" ? "會員推薦" : t === "campaign" ? "活動折扣" : t === "consultant" ? "顧問" : t;
}
